// File: src/components/activity/ActivityFeed/ActivityFeed.tsx

import React from "react";
import styled, { keyframes } from "styled-components";
import { formatDistanceToNow } from "date-fns";
import { Avatar, Typography, Card } from "../..";

export interface ActivityItem2 {
  id: string;
  user: {
    name: string;
    avatarUrl?: string;
  };
  action: string;
  target?: string;
  timestamp: string | Date;
  metadata?: Record<string, any>;
}

export interface ActivityFeedProps2 {
  items: ActivityItem2[];
  onItemClick?: (item: ActivityItem2) => void;
}

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FeedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
`;

const ActivityCard = styled(Card)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.radii.lg};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: box-shadow 0.3s ease, transform 0.2s ease;
  animation: ${slideUp} 0.3s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevations.md};
    transform: scale(1.01);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LeftBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ActionText = styled(Typography)`
  line-height: 1.4;
  word-break: break-word;
`;

const Timestamp = styled(Typography)`
//   font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MetaInfo = styled(Typography)`
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.xs};
  }
`;

export const ActivityFeed2: React.FC<ActivityFeedProps2> = ({ items, onItemClick }) => {
  return (
    <FeedWrapper>
      {items.map((item) => (
        <ActivityCard key={item.id} onClick={() => onItemClick?.(item)}>
          <LeftBlock>
            <Avatar size="md" src={item.user.avatarUrl} name={item.user.name} />
            <InfoBlock>
              <ActionText as="div" variant="body" weight="medium">
                <strong>{item.user.name}</strong>{" "}
                <span
                  style={{
                    fontWeight: 400,
                    color: item.action.toLowerCase().includes("xp")
                      ? "#66BB6A"
                      : undefined,
                  }}
                >
                  {item.action}
                </span>{" "}
                {item.target && <strong>{item.target}</strong>}
              </ActionText>
              <Timestamp variant="overline">
                {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
              </Timestamp>
            </InfoBlock>
          </LeftBlock>

          {item.metadata?.info && (
            <MetaInfo variant="caption">{item.metadata.info}</MetaInfo>
          )}
        </ActivityCard>
      ))}
    </FeedWrapper>
  );
};